import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { PaletteConfig } from '@framework/analytics/models/palette.model';
import { capitalizePaletteLabel } from '@framework/analytics/defaults/palette.default';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { isErrorExistingFromGQLResponse } from '@jepto/shared/utils/collection';

import { listPalettes } from '../graphql/queries/list-palettes';
import type {
	PaletteListItem,
	PaletteListPagination,
	PalettesListResponse,
} from '../models/palette-list.model';

interface ListPalettesGqlItem {
	paletteId?: string;
	label?: string;
	light?: string[];
	dark?: string[];
	reportUsage?: number | null;
	themeUsage?: number | null;
}

interface ListPalettesPaginationGql {
	limit?: number | null;
	pageSize?: number | null;
	totalItems?: number | null;
	page?: number | null;
}

@Injectable({ providedIn: 'root' })
export class JepPaletteQueriesService {
	private readonly jepRequest = inject(JepRequestService);

	/**
	 * Batch size for editor/chart paths that need the full palette list.
	 * `listPalettes()` walks pages until a batch is smaller than this (or a safety cap).
	 */
	private static readonly CHART_PALETTE_LIST_PAGE_SIZE = 500;

	/**
	 * Guard against a misbehaving API that always returns a full page.
	 * (500 × 200 = 100k palettes — adjust if product needs more.)
	 */
	private static readonly CHART_PALETTE_LIST_MAX_PAGES = 200;

	/**
	 * Fetches client-scoped palettes from `listItems(clientId).palettes.items`.
	 * Loads all pages (concatenated) so large orgs are not silently truncated.
	 * @param clientId Optional client id; pass `null` to omit filtering if the API allows.
	 */
	async listPalettes(clientId?: string | null): Promise<PaletteConfig[]> {
		const pageSize = JepPaletteQueriesService.CHART_PALETTE_LIST_PAGE_SIZE;
		const maxPages = JepPaletteQueriesService.CHART_PALETTE_LIST_MAX_PAGES;
		const all: PaletteListItem[] = [];
		let page = 1;

		for (;;) {
			const { items, pagination } = await this.listPalettesPage(
				{ page, pageSize },
				clientId
			);
			all.push(...items);

			const batchFull = items.length === pageSize;
			const total = pagination.totalItems;
			const haveTotal =
				typeof total === 'number' &&
				Number.isFinite(total) &&
				total >= 0;
			const gotAllFromTotal = haveTotal && all.length >= total;

			if (!batchFull || gotAllFromTotal || page >= maxPages) {
				break;
			}
			page += 1;
		}

		return all.map(
			({ paletteId, label, light, dark }): PaletteConfig => ({
				paletteId,
				label,
				light,
				dark,
			})
		);
	}

	/**
	 * Paginated palettes including usage counts for list UIs.
	 */
	async listPalettesPage(
		pagination: { page: number; pageSize: number },
		clientId?: string | null
	): Promise<PalettesListResponse> {
		const paginationInput = {
			limit: pagination.pageSize,
			offset: (pagination.page - 1) * pagination.pageSize,
		};

		const variables: {
			clientId: string | null;
			pagination: typeof paginationInput;
		} = {
			clientId: clientId ?? null,
			pagination: paginationInput,
		};

		const gqlResponse = await firstValueFrom(
			this.jepRequest.query({
				query: listPalettes,
				variables,
				fetchPolicy: 'network-only',
			})
		);

		if (isErrorExistingFromGQLResponse(gqlResponse)) {
			const msg =
				(Array.isArray(gqlResponse.errors) && gqlResponse.errors[0]?.message) ??
				'Could not load palettes';
			throw new Error(msg);
		}

		const palettes = gqlResponse?.data?.listItems?.palettes as
			| {
					items?: ListPalettesGqlItem[] | null;
					pagination?: ListPalettesPaginationGql | null;
			  }
			| undefined;

		const rawItems = palettes?.items ?? [];
		const paginationData = palettes?.pagination;

		const mappedItems = rawItems
			.map(this.mapGqlItemToPaletteListItem)
			.filter((p): p is PaletteListItem => p !== null);

		const listPagination: PaletteListPagination = {
			page: paginationData?.page ?? pagination.page,
			pageSize: paginationData?.pageSize ?? pagination.pageSize,
			totalItems: paginationData?.totalItems ?? mappedItems.length,
			limit: paginationData?.limit ?? pagination.pageSize,
		};

		return { items: mappedItems, pagination: listPagination };
	}

	private mapGqlItemToPaletteListItem(
		item: ListPalettesGqlItem
	): PaletteListItem | null {
		const paletteId = item.paletteId?.trim();
		const label = item.label?.trim();
		const light = Array.isArray(item.light) ? item.light : [];
		const dark = Array.isArray(item.dark) ? item.dark : [];
		if (!paletteId || !label || light.length === 0 || dark.length === 0) {
			return null;
		}
		const reportUsage = Number(item.reportUsage ?? 0);
		const themeUsage = Number(item.themeUsage ?? 0);
		return {
			paletteId,
			label: capitalizePaletteLabel(label),
			light,
			dark,
			reportUsage: Number.isFinite(reportUsage) ? reportUsage : 0,
			themeUsage: Number.isFinite(themeUsage) ? themeUsage : 0,
		};
	}
}
